import React, { useState, useEffect } from 'react';
import { 
  ArrowUp, 
  ArrowDown, 
  DollarSign, 
  ShoppingCart, 
  TrendingUp,
  CalendarIcon,
  Lock,
  Download,
  Sparkles
} from 'lucide-react';
import { useAuth } from '../../common/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import PermissionDenied from '../../common/components/PermissionDenied';
import { API_URL } from '../../api/config';
import SessionTimerIndicator from '../settings/settingModal/SessionTimerIndicator';

const CHART_COLORS = {
  pie: ['#3B82F6', '#60A5FA', '#93C5FD', '#DBEAFE', '#6366F1', '#EC4899', '#8B5CF6', '#14B8A6', '#F97316', '#A855F7'],
  bar: ['#DBEAFE', '#DBEAFE', '#DBEAFE', '#3B82F6', '#93C5FD', '#3B82F6', '#DBEAFE']
};

const ExpenseReport = () => {
  const { userToken: token } = useAuth();
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]   
  });
  const [stats, setStats] = useState({});
  const [itemStats, setItemStats] = useState([]);
  const [chartType, setChartType] = useState('pie');
  const [sortConfig, setSortConfig] = useState({ key: 'count', direction: 'desc' });
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllItems, setShowAllItems] = useState(false);
  const [minPercentageThreshold, setMinPercentageThreshold] = useState(2);
  const [activeRange, setActiveRange] = useState('Last 7 Days');

  useEffect(() => {
    if (token) {
      checkSubscriptionStatus();
    }
  }, [token]);

  useEffect(() => {
    if (token && subscriptionStatus && subscriptionStatus.hasAccess) {
      fetchData();
    }
  }, [dateRange, token, subscriptionStatus]);

  const checkSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/subscription/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch subscription status');
      }
      
      const data = await response.json();
      setSubscriptionStatus(data);
    } catch (error) {
      console.error('Error checking subscription status:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = () => {
    navigate('/subscription');
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const startDate = new Date(dateRange.startDate);
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date(dateRange.endDate);
      endDate.setHours(23, 59, 59, 999);

      const response = await fetch(
        `${API_URL}/expenses?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied: contact admin for permissions');
        }
        throw new Error('Failed to fetch expenses');
      }
      
      const result = await response.json();
      const rawData = result.data || [];
      
      const data = rawData.map(expense => ({
        id: expense.id,
        item: expense.item,
        quantity: expense.quantity,
        price: expense.price,
        totalPrice: expense.total_price, 
        modeOfPayment: expense.mode_of_payment, 
        status: expense.status,
        date: expense.date,
        createdAt: expense.created_at,
        totalDailyExpense: expense.total_daily_expense
      }));
      
      setExpenses(data);
      
      const itemStats = {};
      data.forEach(expense => {
        if (!itemStats[expense.item]) {
          itemStats[expense.item] = {
            item: expense.item,
            count: 0,
            totalSpent: 0, 
            avgPrice: 0,
            firstPurchase: new Date(expense.date),
            lastPurchase: new Date(expense.date),
            frequency: 0
          };
        }
        
        const stat = itemStats[expense.item];
        const expenseAmount = parseFloat(expense.totalPrice) || 0;
        stat.count += parseFloat(expense.quantity) || 1;
        stat.totalSpent += expenseAmount;
        stat.avgPrice = stat.totalSpent / stat.count;
        stat.firstPurchase = new Date(Math.min(stat.firstPurchase, new Date(expense.date)));
        stat.lastPurchase = new Date(Math.max(stat.lastPurchase, new Date(expense.date)));
      });
      
      Object.values(itemStats).forEach(stat => {
        const monthsDiff = (stat.lastPurchase - stat.firstPurchase) / (1000 * 60 * 60 * 24 * 30.44);
        stat.frequency = monthsDiff > 0 ? (stat.count / monthsDiff).toFixed(2) : stat.count;
      });

      setItemStats(Object.values(itemStats));
      
      const totalExpensesSum = data.reduce((sum, expense) => sum + (parseFloat(expense.totalPrice) || 0), 0);
      
      const filteredStats = {
        totalExpenses: totalExpensesSum,
        avgExpense: data.length > 0 ? totalExpensesSum / data.length : 0,
        maxExpense: data.length > 0 ? Math.max(...data.map(expense => parseFloat(expense.totalPrice) || 0)) : 0,
        minExpense: data.length > 0 ? Math.min(...data.map(expense => parseFloat(expense.totalPrice) || 0)) : 0,
        count: data.length
      };
      
      setStats(filteredStats);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
    setActiveRange('Custom Range');
  };

  const setQuickDateRange = (days, label) => {
    const endDate = new Date();
    const startDate = new Date();
    
    if (days === 0) {
      startDate.setHours(0, 0, 0, 0);
    } else {
      startDate.setDate(endDate.getDate() - days);
    }
    
    setDateRange({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    });
    setActiveRange(label);
  };

  const getExpensesByCategory = () => {
    const categories = {};
    expenses.forEach(expense => {
      const amount = parseFloat(expense.totalPrice) || 0;
      categories[expense.item] = (categories[expense.item] || 0) + amount;
    });
    
    const sortedCategories = Object.entries(categories)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const total = sortedCategories.reduce((sum, item) => sum + item.value, 0);
    
    const significantItems = [];
    let othersValue = 0;
    const threshold = showAllItems ? 0 : (total * minPercentageThreshold / 100);
    
    sortedCategories.forEach(item => {
      if (item.value >= threshold && significantItems.length < 10) {
        significantItems.push({
          ...item,
          label: `${item.name}: ₹${item.value.toFixed(0)}`,
          percentage: (item.value / total * 100).toFixed(1)
        });
      } else {
        othersValue += item.value;
      }
    });
    
    if (othersValue > 0) {
      significantItems.push({
        name: 'Others',
        value: othersValue,
        label: `Others: ₹${othersValue.toFixed(0)}`,
        percentage: (othersValue / total * 100).toFixed(1)
      });
    }
    
    return significantItems;
  };

  const getTopCategory = () => {
    const categories = getExpensesByCategory();
    if (categories.length === 0) return { name: 'N/A', percentage: '0' };
    return { name: categories[0].name, percentage: categories[0].percentage };
  };

  const sortData = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortedItems = () => {
    const sortedItems = [...itemStats];
    const { key, direction } = sortConfig;
    
    return sortedItems.sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const DonutChartComponent = () => {
    const data = getExpensesByCategory().slice(0, 3);
    const total = data.reduce((sum, item) => sum + item.value, 0);
    const topPercentage = data.length > 0 ? data[0].percentage : '0';
    
    if (data.length === 0) {
      return (
        <div className="flex justify-center items-center h-64">
          <p className="text-xs font-bold text-gray-400">No data available</p>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center">
            <div className="min-h-screen bg-slate-50 p-8">
            <SessionTimerIndicator module="expenseReport" />
        <div className="relative flex items-center justify-center mb-8">
          <div className="w-56 h-56 rounded-full relative flex items-center justify-center">
            <svg className="w-56 h-56 transform -rotate-90">
              {data.map((item, index) => {
                const percentage = parseFloat(item.percentage) / 100;
                const circumference = 2 * Math.PI * 90;
                const offset = data.slice(0, index).reduce((sum, d) => sum + (parseFloat(d.percentage) / 100), 0) * circumference;
                const dashArray = `${percentage * circumference} ${circumference}`;
                
                return (
                  <circle
                    key={index}
                    cx="112"
                    cy="112"
                    r="90"
                    fill="none"
                    stroke={CHART_COLORS.pie[index]}
                    strokeWidth="28"
                    strokeDasharray={dashArray}
                    strokeDashoffset={-offset}
                    className="transition-all duration-500"
                  />
                );
              })}
            </svg>
            <div className="absolute flex flex-col items-center">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Top Spend</p>
              <p className="text-5xl font-black text-gray-900">{topPercentage}%</p>
            </div>
          </div>
        </div>

        <div className="space-y-4 w-full">
          {data.map((item, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: CHART_COLORS.pie[index] }}
                ></div>
                <span className="text-sm font-semibold text-gray-600">{item.name}</span>
              </div>
              <span className="text-sm font-black text-gray-900">₹{item.value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
            </div>
          ))}
        </div>
      </div>
      </div>
    );
  };

  const BarChartComponent = () => {
    const data = expenses.slice(0, 7).reverse();
    const maxValue = Math.max(...data.map(expense => parseFloat(expense.totalPrice) || 0), 1);
    
    if (data.length === 0) {
      return (
        <div className="flex justify-center items-center h-64">
          <p className="text-xs font-bold text-gray-400">No expense data available</p>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Expense Trend</h3>
          <button className="text-xs font-bold text-blue-600 hover:text-blue-800 uppercase tracking-wide">
            View Details
          </button>
        </div>
        <div className="flex-1 flex items-end justify-between gap-4 px-4 pb-4">
          {data.map((expense, index) => {
            const height = ((parseFloat(expense.totalPrice) || 0) / maxValue) * 100;
            const isHighest = parseFloat(expense.totalPrice) === maxValue;
            const date = new Date(expense.date);
            const dateLabel = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            
            return (
              <div key={index} className="flex-1 flex flex-col items-center gap-4 group">
                <div className="w-full relative bg-gray-50 rounded-2xl h-72 flex items-end overflow-hidden">
                  <div 
                    className={`w-full ${isHighest ? 'bg-blue-600 shadow-lg shadow-blue-200' : 'bg-blue-200/70'} rounded-2xl transition-all duration-700 group-hover:bg-blue-500`}
                    style={{ height: `${height}%` }}
                  ></div>
                </div>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                  {dateLabel}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-sm border border-gray-100 p-8 text-center">
          <div className="text-red-500 mb-4 text-xl font-bold">Authentication Required</div>
          <p className="text-sm font-medium text-gray-400">Please log in to view the expense report.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm font-semibold text-gray-600">Loading expense report...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <PermissionDenied
        variant="compact"
        title="Expense Report Error"
        message={error}
        onRetry={fetchData}
        onGoHome={() => navigate('/')}
        isRetrying={loading}
      />
    );
  }
  
  if (!subscriptionStatus || !subscriptionStatus.hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-sm border border-gray-100 p-8 text-center">
          <div className="bg-blue-50 p-4 rounded-2xl w-16 h-16 mx-auto mb-6 flex items-center justify-center">
            <Lock className="text-blue-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Premium Feature</h2>
          <div className="bg-blue-50/50 border border-blue-100 p-6 rounded-2xl mb-6">
            <p className="text-sm font-medium text-gray-700 leading-relaxed">
              Expense Report is a premium feature. Subscribe now or start your free trial to access detailed analytics and insights.
            </p>
          </div>
          <div className="space-y-3">
            {subscriptionStatus?.canStartTrial && (
              <p className="text-xs font-bold text-green-600 uppercase tracking-wide">
                ✓ Free trial available - No credit card required
              </p>
            )}
            <button 
              onClick={handleSubscribe}
              className="w-full px-6 py-3 bg-gray-900 text-white font-bold text-sm rounded-xl shadow-lg hover:bg-gray-800 transition-all"
            >
              {subscriptionStatus?.canStartTrial ? 'Start Free Trial' : 'Subscribe Now'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const topCategory = getTopCategory();

  return (
    <div className="bg-gray-50 min-h-screen pb-16">
      <div className="p-8 space-y-8">
        {/* Header */}
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Expense Reports</h1>
              <p className="text-sm font-medium text-gray-400 mt-1">Overview of your financial outflow and spending patterns.</p>
            </div>
            <button className="flex items-center gap-2 bg-white border border-gray-200 px-5 py-2.5 rounded-xl font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-all">
              <Download size={18} />
              Export Report
            </button>
          </div>

          {/* Date Range Filters & AI Insight */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex items-center gap-1.5 bg-white p-1 rounded-2xl shadow-sm border border-gray-100 w-fit">
              <button
                onClick={() => setQuickDateRange(0, 'This Month')}
                className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all ${
                  activeRange === 'This Month' ? 'bg-gray-900 text-white shadow-lg' : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                This Month
              </button>
              <button
                onClick={() => setQuickDateRange(6, 'Last 7 Days')}
                className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all ${
                  activeRange === 'Last 7 Days' ? 'bg-gray-900 text-white shadow-lg' : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                Last 7 Days
              </button>
              <button
                onClick={() => setQuickDateRange(29, 'Last Month')}
                className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all ${
                  activeRange === 'Last Month' ? 'bg-gray-900 text-white shadow-lg' : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                Last Month
              </button>
              <button
                onClick={() => setQuickDateRange(89, 'Last 3 Months')}
                className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all ${
                  activeRange === 'Last 3 Months' ? 'bg-gray-900 text-white shadow-lg' : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                Last 3 Months
              </button>
              <div className="h-6 w-px bg-gray-100 mx-2"></div>
              <button className="flex items-center gap-2 px-4 py-2.5 text-xs font-bold text-gray-500 hover:text-gray-900">
                <CalendarIcon size={16} />
                Custom Range
              </button>
            </div>

            {itemStats.length > 0 && (
              <div className="flex-1 max-w-lg bg-indigo-50/50 border border-indigo-100 p-4 rounded-3xl flex items-start gap-4">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-blue-600 shadow-sm shrink-0">
                  <Sparkles size={20} />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-0.5">AI Insight</p>
                  <p className="text-xs font-semibold text-gray-700 leading-relaxed">
                    Spending on <span className="font-bold text-gray-900">{topCategory.name}</span> is your top category at {topCategory.percentage}% of total expenses.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center">
                <DollarSign size={18} className="text-red-500" />
              </div>
              {stats.totalExpenses > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-600 rounded-lg text-[10px] font-bold">
                  <TrendingUp size={12} />
                  4.2%
                </div>
              )}
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Total Expenses</p>
              <h3 className="text-3xl font-bold text-gray-900">₹{stats.totalExpenses?.toLocaleString('en-IN', { maximumFractionDigits: 2 }) || 0}</h3>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 bg-orange-50 rounded-2xl flex items-center justify-center">
                <TrendingUp size={18} className="text-orange-500" />
              </div>
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Avg Daily Spend</p>
              <h3 className="text-3xl font-bold text-gray-900">₹{stats.avgExpense?.toLocaleString('en-IN', { maximumFractionDigits: 2 }) || 0}</h3>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center">
                <ShoppingCart size={18} className="text-blue-500" />
              </div>
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Top Category</p>
              <div className="flex items-baseline gap-2">
                <h3 className="text-3xl font-bold text-gray-900">{topCategory.name}</h3>
                <span className="text-sm font-semibold text-gray-400">{topCategory.percentage}%</span>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center">
                <ArrowUp size={18} className="text-gray-500" />
              </div>
              {expenses.filter(e => e.status === 'pending').length > 0 && (
                <div className="px-2 py-1 bg-orange-50 text-orange-600 rounded-lg text-[9px] font-bold uppercase tracking-tight">
                  {expenses.filter(e => e.status === 'pending').length} Pending
                </div>
              )}
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Unpaid Invoices</p>
              <h3 className="text-3xl font-bold text-gray-900">₹{expenses.filter(e => e.status === 'pending').reduce((sum, e) => sum + parseFloat(e.totalPrice || 0), 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</h3>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Bar Chart - Takes 2 columns */}
          <div className="lg:col-span-2 bg-white p-8 rounded-3xl border border-gray-100 shadow-sm flex flex-col h-[480px]">
            <BarChartComponent />
          </div>

          {/* Donut Chart - Takes 1 column */}
          <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm flex flex-col h-[480px]">
            <h3 className="text-xl font-bold text-gray-900 mb-8">Spend by Category</h3>
            <DonutChartComponent />
          </div>
        </div>

        {/* Detailed Statistics Table */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-8 flex items-center justify-between border-b border-gray-50">
            <h3 className="text-xl font-bold text-gray-900">Recent Transactions</h3>
            <button className="p-2.5 text-gray-400 hover:text-gray-900 transition-colors bg-gray-50 rounded-xl">
              <Download size={18} />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-8 py-4 text-left text-[10px] font-bold text-gray-400 uppercase tracking-widest cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => sortData('item')}
                  >
                    <div className="flex items-center">
                      Item
                      {sortConfig.key === 'item' && (
                        sortConfig.direction === 'asc'
                          ? <ArrowUp size={14} className="ml-2" />
                          : <ArrowDown size={14} className="ml-2" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-8 py-4 text-left text-[10px] font-bold text-gray-400 uppercase tracking-widest cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => sortData('count')}
                  >
                    <div className="flex items-center">
                      Quantity
                      {sortConfig.key === 'count' && (
                        sortConfig.direction === 'asc'
                          ? <ArrowUp size={14} className="ml-2" />
                          : <ArrowDown size={14} className="ml-2" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-8 py-4 text-left text-[10px] font-bold text-gray-400 uppercase tracking-widest cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => sortData('totalSpent')}
                  >
                    <div className="flex items-center">
                      Total Spent
                      {sortConfig.key === 'totalSpent' && (
sortConfig.direction === 'asc'
? <ArrowUp size={14} className="ml-2" />
: <ArrowDown size={14} className="ml-2" />
)}
</div>
</th>
<th
className="px-8 py-4 text-left text-[10px] font-bold text-gray-400 uppercase tracking-widest cursor-pointer hover:bg-gray-100 transition-colors"
onClick={() => sortData('avgPrice')}
>
<div className="flex items-center">
Avg Price
{sortConfig.key === 'avgPrice' && (
sortConfig.direction === 'asc'
? <ArrowUp size={14} className="ml-2" />
: <ArrowDown size={14} className="ml-2" />
)}
</div>
</th>
</tr>
</thead>
<tbody className="bg-white divide-y divide-gray-50">
{getSortedItems().map((item, index) => (
<tr
                 key={item.item}
                 className="hover:bg-blue-50 transition-colors"
               >
<td className="px-8 py-4 whitespace-nowrap">
<div className="text-sm font-semibold text-gray-900">{item.item}</div>
</td>
<td className="px-8 py-4 whitespace-nowrap text-sm font-semibold text-gray-600">{item.count.toLocaleString('en-IN')}</td>
<td className="px-8 py-4 whitespace-nowrap text-sm font-bold text-gray-900">₹{item.totalSpent.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
<td className="px-8 py-4 whitespace-nowrap text-sm font-semibold text-gray-600">₹{item.avgPrice.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
</tr>
))}
</tbody>
</table>
{itemStats.length === 0 && (
<div className="text-center py-16">
<p className="text-xs font-bold text-gray-300 uppercase tracking-[0.2em]">Detailed transaction log appears here...</p>
</div>
)}
</div>
</div>
</div>
</div>
);
};
export default ExpenseReport;